import Seo from "@/components/Seo";
import { Reveal, Eyebrow, Marquee } from "@/components/motion";
import { Target, Eye, BadgeCheck, HeartHandshake, ShieldCheck, Users, RefreshCcw } from "lucide-react";

const values = [
  { icon: BadgeCheck, label: "Quality" },
  { icon: ShieldCheck, label: "Reliability" },
  { icon: Users, label: "Farmer Focus" },
  { icon: HeartHandshake, label: "Integrity" },
  { icon: Target, label: "Partnership" },
  { icon: RefreshCcw, label: "Continuous Improvement" },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — ICROPSIA India Pvt Ltd"
        description="ICROPSIA India Pvt Ltd is an agricultural seed company providing quality-focused maize and wheat seed products to farmers and agricultural partners in Bihar."
      />
      <section className="relative pt-44 pb-24 bg-brand-ink overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600284858934-d05fde407182?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt="Golden wheat field"
            className="w-full h-full object-cover opacity-25"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 to-brand-ink" />
        </div>
        <div className="container-site relative">
          <Reveal>
            <Eyebrow light>About ICROPSIA</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-3xl">
              Growing With Farmers. Building Trust Through <span className="font-editorial italic font-semibold text-brand-wheat">Quality.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
              ICROPSIA India Pvt Ltd is an agricultural seed company focused on providing quality-focused maize and
              wheat seed products to farmers and agricultural partners in Bihar.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["QUALITY", "RELIABILITY", "INTEGRITY", "PARTNERSHIP", "FARMER FOCUS", "BIHAR"]} />

      <section className="py-20 sm:py-28">
        <div className="container-site grid md:grid-cols-2 gap-7">
          <Reveal>
            <div className="h-full rounded-3xl bg-brand-beige border border-brand-deep/10 p-8 sm:p-10">
              <span className="w-12 h-12 rounded-2xl bg-brand-deep flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-brand-wheat" />
              </span>
              <h2 className="font-display text-2xl font-extrabold text-brand-deep tracking-tight">Our Mission</h2>
              <p className="mt-4 text-brand-charcoal/70 leading-relaxed">
                To empower farming communities through dependable seed products, professional service, and long-term
                agricultural partnerships.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl bg-brand-deep text-white p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute inset-0 grain opacity-20" />
              <span className="relative w-12 h-12 rounded-2xl bg-brand-gold flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-brand-deep" />
              </span>
              <h2 className="relative font-display text-2xl font-extrabold tracking-tight">Our Vision</h2>
              <p className="relative mt-4 text-white/75 leading-relaxed">
                To become a trusted agricultural seed brand recognized for quality, reliability, and strong
                relationships across India's farming communities.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-site">
          <Reveal className="max-w-2xl">
            <Eyebrow>What Guides Us</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-deep">Our Values</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {values.map(({ icon: Icon, label }, i) => (
              <Reveal key={label} delay={i * 0.06}>
                <div className="rounded-2xl border border-brand-deep/10 bg-white p-5 text-center hover:border-brand-gold/60 hover:shadow-lg hover:shadow-brand-deep/5 transition-all group">
                  <Icon className="w-6 h-6 text-brand-deep mx-auto mb-3 group-hover:text-brand-gold transition-colors" />
                  <span className="font-display font-bold text-sm text-brand-deep">{label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-site">
          <Reveal>
            <div className="rounded-3xl border border-dashed border-brand-deep/25 bg-brand-beige/50 p-8 sm:p-10">
              <Eyebrow>Our Story</Eyebrow>
              <p className="mt-4 text-brand-charcoal/70 leading-relaxed max-w-3xl">
                Our story is being written season by season, together with farmers and agricultural partners across
                Bihar. Company milestones, certifications and history will be published here as they are formally
                documented — because trust is built on what can be verified.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
